import React, { useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { reset } from '../redux/userSlice';
import { reset as insuranceReset } from '../redux/insuranceSlice';
import { reset as stripeReset } from '../redux/stripeSlice';
import { reset as portalReset } from '../redux/portalSlice';
import ContinueButton from "../components/ContinueButton";
import { Box, Typography, Link, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { useState, useCallback } from 'react';
import pictionLogo from "../assets/piction-logo.png";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

const insurerList = ["United Healthcare", "Blue Cross Blue Shield", "Anthem", "Aetna", "Cigna", "Unicare / Wellpoint", "Ambetter Sunshine Health"];
const pressList = ["wsj", "mit", "forbes", "axios"];

const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

const MainLanding = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isShortScreen = useMediaQuery('(max-height: 700px)');
    const [loadedImages, setLoadedImages] = useState({});
    const [loadedPressLogos, setLoadedPressLogos] = useState({});

    const loadImage = useCallback(async (insurer) => {
        try {
            const src = require(`../assets/insure-logos/${insurer.replace(/\s+/g, "-").replace(/\//g, "")}.jpg`);
            await preloadImage(src);
            setLoadedImages(prev => ({ ...prev, [insurer]: src }));
        } catch (error) {
            console.error(`Failed to load image for ${insurer}:`, error);
        }
    }, []);

    const loadPressLogo = useCallback(async (press) => {
        try {
            const src = require(`../assets/icons/${press}.png`);
            await preloadImage(src);
            setLoadedPressLogos(prev => ({ ...prev, [press]: src }));
        } catch (error) {
            console.error(`Failed to load press logo for ${press}:`, error);
        }
    }, []);

    useEffect(() => {
        insurerList.forEach(loadImage);
        pressList.forEach(loadPressLogo);
    }, [loadImage, loadPressLogo]);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(reset());
        dispatch(insuranceReset());
        dispatch(stripeReset());
        dispatch(portalReset());
    }, [dispatch]);

    return (
        <Box sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
            pb: isShortScreen ? 4 : 3
        }}>
            <Box sx={{
                width: '100%',
                bgcolor: '#2200BB',
                color: 'white',
                py: isDesktop ? (isShortScreen ? 1.5 : 3) : 1.5,
                textAlign: 'center',
                mb: isShortScreen ? 1 : 2,
            }}>
                <Typography sx={{ fontSize: isDesktop ? (isShortScreen ? '1rem' : '1.2rem') : '0.9rem' }}>
                    Many insurances accepted or just $119 out-of-pocket
                </Typography>
            </Box>

            <Box sx={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box sx={{
                    maxWidth: isDesktop ? '800px' : '600px',
                    width: '100%',
                    px: 2,
                    margin: '0 auto'
                }}>
                    <Box sx={{
                        textAlign: 'center',
                        mb: isShortScreen ? 1 : (isDesktop ? 4 : 3)
                    }}>
                        <img
                            src={pictionLogo}
                            alt="Piction Health Logo"
                            style={{
                                maxWidth: isDesktop ? (isShortScreen ? '120px' : '150px') : '110px',
                                marginBottom: isShortScreen ? '8px' : '16px'
                            }}
                        />
                    </Box>

                    <Box sx={{
                        textAlign: 'center',
                        mb: isShortScreen ? 2 : 4
                    }}>
                        <Typography
                            variant={isDesktop ? (isShortScreen ? 'h5' : 'h4') : 'h5'}
                            sx={{
                                fontFamily: "Sentient",
                                mb: isShortScreen ? 0.5 : 1,
                                fontWeight: 400,
                                fontSize: isShortScreen ? '1.5rem' : undefined
                            }}
                        >
                            Modern Dermatology Clinic
                        </Typography>
                        <Typography
                            variant={isDesktop ? (isShortScreen ? 'h5' : 'h4') : 'h5'}
                            sx={{
                                fontFamily: "Sentient",
                                mb: isShortScreen ? 1 : 3,
                                fontWeight: 400,
                                fontSize: isShortScreen ? '1.5rem' : undefined
                            }}
                        >
                            Without A Wait
                        </Typography>
                        <Typography
                            variant={isDesktop ? (isShortScreen ? 'h7' : 'h5') : 'h7'}
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 400,
                                maxWidth: isDesktop ? '700px' : 'none',
                                margin: '0 auto',
                                mb: isShortScreen ? 4 : (isDesktop ? 6 : 5),
                                fontSize: isShortScreen ? '1rem' : undefined
                            }}
                        >
                            Get in-depth 1:1 treatment with a board-certified dermatologist, from the comfort of home
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3, mb: 3, zIndex: 9999, }}>
                            <ContinueButton
                                onClick={() => setPageAndDeferNavigate(dispatch, '/concern', navigate)}
                                title="Get Treatment"
                                selected={true}
                                sx={{
                                    width: isDesktop ? (isShortScreen ? '200px' : '250px') : '180px',
                                    height: isDesktop ? (isShortScreen ? '40px' : '50px') : '40px',
                                    fontSize: isShortScreen ? '1rem' : (isDesktop ? '1.1rem' : '0.9rem')
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    mb: isShortScreen ? 2 : 4,
                    bgcolor: '#EDEDED',
                    py: isShortScreen ? 0.5 : 1,
                    width: '100vw',
                    position: 'relative',
                    left: '50%',
                    right: '50%',
                    marginLeft: '-50vw',
                    marginRight: '-50vw'
                }}>
                    <Box sx={{
                        maxWidth: isDesktop ? '800px' : '600px',
                        margin: '0 auto',
                        px: 4
                    }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: isDesktop ? (isShortScreen ? '20px' : '30px') : '15px',
                            alignItems: 'center'
                        }}>
                            {pressList.map((press) => (
                                <Box
                                    key={press}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: isDesktop ? (isShortScreen ? '40px' : '50px') : '35px'
                                    }}
                                >
                                    {loadedPressLogos[press] ? (
                                        <img
                                            src={loadedPressLogos[press]}
                                            alt={press.toUpperCase()}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                                filter: press !== 'wsj' && "grayscale(100%) brightness(0.6)",
                                                backgroundColor: press === 'wsj' ? 'white' : 'transparent',
                                            }}
                                        />
                                    ) : (
                                        <Skeleton variant="rectangular" width="100%" height="100%" />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    textAlign: 'center',
                    mb: isShortScreen ? 2 : 4,
                    bgcolor: '#EDEDED',
                    py: isDesktop ? (isShortScreen ? 2 : 4) : 2,
                    px: 4,
                    width: '100vw',
                    position: 'relative',
                    left: '50%',
                    right: '50%',
                    marginLeft: '-50vw',
                    marginRight: '-50vw'
                }}>
                    <Typography
                        variant={isDesktop ? (isShortScreen ? 'h6' : 'h5') : 'h6'}
                        sx={{
                            mb: isShortScreen ? 0.5 : 1,
                            fontWeight: 400,
                            fontSize: isShortScreen ? '1.2rem' : undefined
                        }}
                    >
                        At Piction Health, we've got you covered
                    </Typography>
                    <Typography sx={{
                        mb: isShortScreen ? 2 : 3,
                        fontWeight: 400,
                        fontSize: isDesktop ? (isShortScreen ? '1rem' : '1.2rem') : '0.9rem'
                    }}>
                        Many major insurance carriers
                    </Typography>
                    <Box sx={{
                        display: isDesktop ? 'grid' : 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                        maxWidth: isDesktop ? '600px' : '500px',
                        margin: '0 auto',
                        gap: 1.5,
                        mb: isDesktop ? 3 : 1,
                    }}>
                        {insurerList.map((insurer) => (
                            <Box
                                key={insurer}
                                sx={{
                                    width: 90,
                                    height: 50,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: isDesktop ? (isShortScreen ? '8px' : '12px') : '2px'
                                }}
                            >
                                {loadedImages[insurer] ? (
                                    <img
                                        src={loadedImages[insurer]}
                                        alt={insurer}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            filter: "grayscale(100%)"
                                        }}
                                    />
                                ) : (
                                    <Skeleton variant="rectangular" width="100%" height="100%" />
                                )}
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{
                        fontWeight: 400,
                        fontSize: isDesktop ? (isShortScreen ? '1rem' : '1.2rem') : '0.9rem'
                    }}>
                        or affordable self-pay at just $119
                    </Typography>
                </Box>

                <Box sx={{
                    position: 'relative',
                    px: 2,
                    pt: 1.5,
                    pb: 0,
                    textAlign: 'center',
                    mt: 'auto'
                }}>
                    <Typography
                        sx={{
                            fontSize: isShortScreen ? '0.65rem' : (isDesktop ? '0.8rem' : '0.72rem'),
                            color: 'text.secondary',
                            maxWidth: isDesktop ? '900px' : 'none',
                            margin: '0 auto',
                            '& a': {
                                color: '#666666',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#333333'
                                }
                            }
                        }}
                    >
                        By continuing, you agree that you're 18 or older and to our{' '}
                        <Link href="https://www.pictionhealth.com/informed-consent" target="_blank" rel="noopener">
                            Patient Consent
                        </Link>
                        ,{' '}
                        <Link href="https://www.pictionhealth.com/npp" target="_blank" rel="noopener">
                            Notice of Privacy Practices
                        </Link>
                        ,{' '}
                        <Link href="https://www.pictionhealth.com/privacy-policy" target="_blank" rel="noopener">
                            Privacy Policy
                        </Link>
                        , and{' '}
                        <Link href="https://www.pictionhealth.com/terms-of-use" target="_blank" rel="noopener">
                            Terms
                        </Link>
                        . By providing your phone number, you consent to receiving appointment reminders & information via text message. You can always opt out of reminders by replying "stop".
                    </Typography>
                </Box>
            </Box>
        </Box >
    );
};

export default MainLanding;
